import { ApiError, UnauthorizedError, getTenantId } from '../../api/client';
import { APP_VERSION } from '../../version';
import { t } from '../../i18n/t';
import { formatDateTimeIL } from '../../utils/dateTime';

// Global error-toast API. THE way to surface an OPERATION failure (save / delete /
// run / import / load) to the user: friendly Hebrew message up front, full technical
// text on demand ("פרטים" / copy / mail to support). Module-level store so it can be
// called from hooks and plain functions; <ToastProvider/> (mounted once in AppShell)
// renders it.

export interface ErrorToastOptions {
  /** Short Hebrew title (e.g. t('errorToast.titleSave')). Derived from the error if absent. */
  title?: string;
  /** Free-text extra context for the technical details (e.g. the screen/action). */
  context?: string;
}

export interface ErrorToastItem {
  id: number;
  key: string;
  title: string;
  body: string;
  technical: string;
  count: number;
  updatedAt: number;
}

const MAX_VISIBLE = 3;
const DEDUPE_MS = 5000;
const HEBREW = /[֐-׿]/;

let seq = 0;
let items: ErrorToastItem[] = [];
const listeners = new Set<() => void>();
let userEmail: string | null = null;
let tenantLabel: string | null = null;

function emit() {
  listeners.forEach((l) => l());
}

export function subscribeToasts(l: () => void): () => void {
  listeners.add(l);
  return () => {
    listeners.delete(l);
  };
}

export function getToastsSnapshot(): ErrorToastItem[] {
  return items;
}

/** Called by ToastProvider so the technical text can carry user + tenant (never tokens). */
export function setToastUserContext(email: string | null, tenant: string | null): void {
  userEmail = email;
  tenantLabel = tenant;
}

export function dismissToast(id: number): void {
  items = items.filter((i) => i.id !== id);
  emit();
}

export function clearToasts(): void {
  items = [];
  emit();
}

function israelTime(d: Date): string {
  return formatDateTimeIL(d, { seconds: true }) || d.toISOString();
}

// Legacy plain Errors look like "POST /x failed: <detail>"; pull <detail> out.
function legacyDetail(message: string): string | null {
  const i = message.indexOf(' failed: ');
  return i >= 0 ? message.slice(i + ' failed: '.length) : null;
}

export function describeError(
  err: unknown,
  opts?: ErrorToastOptions,
): { title: string; body: string; technical: string; key: string } {
  let title = opts?.title ?? t('errorToast.titleOperation');
  let body = t('errorToast.bodyGeneric');
  const lines: string[] = [];
  let sig = '';

  const now = new Date();
  lines.push(`Time (Israel): ${israelTime(now)}`);
  lines.push(`Screen: ${window.location.href}`);
  lines.push(`App version: ${APP_VERSION}`);
  const tenantId = getTenantId();
  lines.push(`Tenant: ${tenantLabel ?? (tenantId !== null ? String(tenantId) : '-')}`);
  lines.push(`User: ${userEmail ?? '-'}`);
  if (opts?.context) lines.push(`Context: ${opts.context}`);

  if (err instanceof UnauthorizedError) {
    if (!opts?.title) title = t('errorToast.titleSession');
    body = t('errorToast.bodySession');
    lines.push(`Request: ${err.path}`, 'Status: 401');
    sig = `401|${err.path}`;
  } else if (err instanceof ApiError) {
    lines.push(`Request: ${err.method} ${err.path}`);
    lines.push(`Status: ${err.status} ${err.statusText}`.trim());
    if (err.serverMessage) lines.push(`Server message: ${err.serverMessage}`);
    if (err.responseBody) lines.push(`Response body: ${err.responseBody}`);
    lines.push(`Error: ${err.message}`);
    sig = `${err.status}|${err.method}|${err.path}|${err.serverMessage ?? ''}`;
    const s = err.status;
    if (s === 0) {
      body = t('errorToast.bodyNetwork'); // fetch() itself failed ("Failed to fetch")
    } else if (s === 401) {
      if (!opts?.title) title = t('errorToast.titleSession');
      body = t('errorToast.bodySession');
    } else if (s === 400 && err.serverMessage && HEBREW.test(err.serverMessage)) {
      body = err.serverMessage; // already user-facing
    } else if (s === 400 || s === 422) {
      body = t('errorToast.bodyValidation');
    } else if (s === 403) {
      body = t('errorToast.bodyForbidden');
    } else if (s === 404) {
      body = t('errorToast.bodyNotFound');
    } else if (s === 409) {
      body = t('errorToast.bodyConflict');
    }
  } else {
    const message = err instanceof Error ? err.message : String(err);
    lines.push(`Error: ${message}`);
    sig = `x|${message}`;
    const detail = legacyDetail(message);
    if (detail && HEBREW.test(detail)) body = detail;
  }

  return { title, body, technical: lines.join('\n'), key: `${title}|${body}|${sig}` };
}

export function showErrorToast(err: unknown, opts?: ErrorToastOptions): void {
  // eslint-disable-next-line no-console
  console.error('[error-toast]', err);
  const d = describeError(err, opts);
  const now = Date.now();
  const dup = items.find((i) => i.key === d.key && now - i.updatedAt < DEDUPE_MS);
  if (dup) {
    items = items.map((i) =>
      i === dup ? { ...i, count: i.count + 1, updatedAt: now, technical: d.technical } : i,
    );
  } else {
    const item: ErrorToastItem = {
      id: ++seq,
      key: d.key,
      title: d.title,
      body: d.body,
      technical: d.technical,
      count: 1,
      updatedAt: now,
    };
    items = [item, ...items].slice(0, MAX_VISIBLE);
  }
  emit();
}
